"use client";

import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";
import { LogisticsMock, DashboardMock } from "./features-lp3";

const MOCKS = [LogisticsMock, DashboardMock];
const MOCK_LABELS = ["Pedidos", "Dashboard"];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

export function HeroLP3() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((prev) => (prev + 1) % MOCKS.length), 14000);
    return () => clearInterval(id);
  }, []);

  const ActiveMock = MOCKS[active];

  return (
    <section className="relative min-h-screen flex flex-col items-center pt-24 pb-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#48c1ce]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#ec399a]/10 rounded-full blur-3xl" />

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:32px_32px]" />

      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-14">
        {/* Copy — centered */}
        <div className="flex flex-col items-center text-center gap-6 max-w-3xl mx-auto">
          {/* Badge */}
          <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#48c1ce]/30 bg-[#48c1ce]/10 text-[#48c1ce] text-xs font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#48c1ce] animate-pulse" />
              Novo · Sistema Operacional do Pós-Venda
            </div>
          </motion.div>

          {/* H1 */}
          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.05]"
          >
            Pare de Apagar
            <br />
            <span className="bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Incêndio.
            </span>
            <br />
            Comece a Crescer.
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg sm:text-xl text-gray-500 dark:text-gray-400 leading-relaxed"
          >
            O Mulply unifica rastreio, atendimento e CRM em uma única tela.{" "}
            <span className="text-gray-700 dark:text-gray-200 font-semibold">
              Sua equipe para de alternar entre 5 abas
            </span>{" "}
            — e seus clientes param de pedir reembolso.
          </motion.p>

          {/* CTAs */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <a
              href="/register"
              className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#48c1ce] hover:bg-[#3aafbc] text-white font-black text-base transition-all duration-200 shadow-xl shadow-[#48c1ce]/30 hover:shadow-[#48c1ce]/50 hover:-translate-y-0.5"
            >
              Começar Agora — Grátis
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#features"
              className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-700 dark:text-gray-200 font-bold text-base transition-all duration-200"
            >
              Ver Como Funciona
            </a>
          </motion.div>

          {/* Trust row */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-4 text-xs font-semibold text-gray-400 dark:text-gray-500"
          >
            {["✓ Sem cartão de crédito", "✓ 14 dias grátis", "✓ Cancele quando quiser"].map((t) => (
              <span key={t}>{t}</span>
            ))}
          </motion.div>
        </div>

        {/* Cycling mock */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.35, ease: "easeOut" }}
          className="relative w-full flex flex-col items-center gap-5"
        >
          <div className="absolute -inset-10 bg-gradient-to-r from-[#48c1ce]/15 to-[#ec399a]/15 blur-3xl rounded-3xl pointer-events-none" />
          {/* translate="no": o carrossel troca a subárvore a cada 14s; nós traduzidos (<font> do Google Translate) no unmount quebram o React DOM */}
          <div className="relative w-full overflow-hidden" translate="no">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.45, ease: "easeInOut" }}
              >
                <ActiveMock />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Indicator dots */}
          <div className="flex items-center gap-2 z-10">
            {MOCK_LABELS.map((label, i) => (
              <button
                key={label}
                onClick={() => setActive(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all duration-300 ${
                  i === active
                    ? "bg-[#48c1ce] text-white shadow-lg shadow-[#48c1ce]/30 scale-105"
                    : "bg-gray-200/70 dark:bg-white/10 text-gray-400 dark:text-gray-500 hover:bg-gray-300/70 dark:hover:bg-white/20"
                }`}
              >
                {i === active && <span className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" />}
                {label}
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
